import { Container, MeshSimple, Texture, type Renderer } from 'pixi.js';

import { fromUtm, project, tileCorner, tileOf, toUtm, type County, type Projection } from '@hotlist/shared';

import type { Viewport } from './viewport.ts';

/**
 * Aerial imagery under the map.
 *
 * Off by default and a deliberate toggle, for two reasons that both matter.
 *
 * **It leaves the machine.** Everything else in this game is local: the county
 * is a file, the record is a database on loopback, and nothing about a player
 * goes anywhere. Turning this on asks a tile server for the ground under the
 * viewport, which tells that server roughly where somebody is looking. That is
 * a small thing and it is not nothing, and it is the player's call rather than
 * a default.
 *
 * **It is a different kind of true.** The road graph, the camera positions and
 * the businesses are the public record, dated and citable. Imagery is a
 * photograph of a day somebody flew over, and the two disagree: a road built
 * since the flight is on the map and not in the picture. Under the map rather
 * than over it, and dimmed, because the record is the thing being played and
 * the picture is context.
 *
 * ## Where the tiles come from
 *
 * Esri's World Imagery service, which serves Web Mercator tiles without an API
 * key or an account. No credential ever exists to leak, and nothing about this
 * client is identified to them beyond the request itself. Attribution is
 * required and is in the citations panel.
 *
 * ## Making Mercator meet a tilted UTM county
 *
 * The county is a flat local frame in UTM meters, and the view applies a yaw
 * and a tilt on top. Tiles arrive as axis-aligned squares in Web Mercator. So
 * each tile is drawn as a **quad**: its four corners go from tile space to
 * latitude and longitude, into the county's UTM zone, into county meters, and
 * through the same `project()` every road uses. Two projections disagreeing
 * over twenty miles would show as imagery sliding against the roads, and
 * the corner-by-corner route is what keeps them nailed together -- a single
 * scale-and-rotate would be right in the middle and wrong at the edges.
 */

const TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile';

/** How many tiles may be drawn at once. */
const TILE_BUDGET = 220;

/**
 * How many tile images to keep after they leave the screen.
 *
 * Tiles used to be destroyed the moment they went out of view, so panning back
 * over ground you had just looked at asked Esri for all of it again -- and
 * driving is mostly moving back and forth over the same few square miles. Six
 * hundred 256px tiles is on the order of a hundred megabytes of texture at the
 * worst, and in practice far less because a session rarely covers that much
 * ground at one zoom.
 *
 * In memory only. Nothing is written to disk: the browser's own HTTP cache
 * decides whether a repeat request goes to the network, and second-guessing it
 * with a persistent store of somebody else's imagery is neither ours to keep
 * nor worth the complexity.
 */
const IMAGE_CACHE = 600;

/** Below this the whole county is on screen and the imagery is a brown smear. */
const MIN_ZOOM = 11;
const MAX_ZOOM = 18;

interface Tile {
  mesh: MeshSimple;
  z: number;
  x: number;
  y: number;
}

export class SatelliteLayer {
  readonly container = new Container();

  private readonly county: County;
  private readonly projection: Projection;
  private readonly zone: number;
  private readonly originEasting: number;
  private readonly originNorthing: number;
  private readonly tiles = new Map<string, Tile>();
  private readonly failed = new Set<string>();
  /**
   * Images for tiles that have left the screen, newest last.
   *
   * A Map iterates in insertion order, which is all an LRU needs: re-reading a
   * key deletes and re-inserts it, so the oldest is always first.
   */
  private readonly cache = new Map<string, Texture>();
  private readonly renderer: Renderer;
  private lastZoom = 0;

  /** Whether the layer is asking for anything at all. */
  on = false;

  constructor(renderer: Renderer, county: County, projection: Projection) {
    this.renderer = renderer;
    this.county = county;
    this.projection = projection;
    const p = county.projection;
    this.zone = p.kind === 'utm' ? p.zone : 0;
    this.originEasting = p.kind === 'utm' ? p.originEasting : 0;
    this.originNorthing = p.kind === 'utm' ? p.originNorthing : 0;
    this.container.alpha = 0.85;
    this.container.visible = false;
  }

  /** Whether this county can be georeferenced at all. A synthetic one cannot. */
  get possible(): boolean {
    return this.zone > 0;
  }

  toggle(): boolean {
    if (!this.possible) return false;
    this.on = !this.on;
    this.container.visible = this.on;
    if (!this.on) this.clear();
    return this.on;
  }

  private clear(): void {
    for (const [key, tile] of this.tiles) this.retire(key, tile);
    this.tiles.clear();
  }

  /** Take a tile off the screen but keep its image, up to the cache size. */
  private retire(key: string, tile: Tile): void {
    const texture = tile.mesh.texture;
    if (texture && texture !== Texture.EMPTY) {
      this.cache.delete(key);
      this.cache.set(key, texture);
      while (this.cache.size > IMAGE_CACHE) {
        const oldest = this.cache.keys().next().value;
        if (oldest === undefined) break;
        this.cache.get(oldest)?.destroy(true);
        this.cache.delete(oldest);
      }
    }
    // The mesh goes; the texture it was showing does not.
    tile.mesh.destroy({ texture: false });
  }

  /** County meters to degrees. */
  private toLatLon(x: number, y: number): { lat: number; lon: number } {
    return fromUtm(this.originEasting + x, this.originNorthing + y, this.zone);
  }

  /** Degrees back to county meters. */
  private toWorld(lat: number, lon: number): { x: number; y: number } {
    const utm = toUtm(lat, lon, this.zone);
    return { x: utm.easting - this.originEasting, y: utm.northing - this.originNorthing };
  }

  /**
   * Which imagery zoom matches the map's current scale.
   *
   * Chosen so one tile pixel is about one screen pixel: coarser and it is
   * visibly soft, finer and it costs four times the requests for detail nobody
   * can see.
   */
  private zoomFor(scale: number): number {
    const middle = this.toLatLon(
      (this.county.bounds.minX + this.county.bounds.maxX) / 2,
      (this.county.bounds.minY + this.county.bounds.maxY) / 2,
    );
    // Ground resolution of a Web Mercator tile pixel at this latitude.
    const groundAtZero = (156543.03392 * Math.cos((middle.lat * Math.PI) / 180)) / 1;
    const wantMetersPerPixel = 1 / scale;
    const zoom = Math.log2(groundAtZero / wantMetersPerPixel);
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(zoom)));
  }

  update(viewport: Viewport): void {
    if (!this.on || !this.possible) return;

    const zoom = this.zoomFor(viewport.scale);
    if (zoom !== this.lastZoom) {
      // A zoom change replaces the whole set. Keeping two levels alive at once
      // would double the requests to show the same ground twice.
      this.clear();
      this.lastZoom = zoom;
    }

    // The visible ground, in county meters, from the four screen corners --
    // the view is rotated, so its world footprint is not axis-aligned and the
    // bounding box of the corners is what has to be covered.
    const bounds = viewport.visibleBounds(0);
    const corners = [
      viewport.toWorld(0, 0),
      viewport.toWorld(viewport.width, 0),
      viewport.toWorld(0, viewport.height),
      viewport.toWorld(viewport.width, viewport.height),
    ];
    void bounds;

    let minTx = Infinity;
    let maxTx = -Infinity;
    let minTy = Infinity;
    let maxTy = -Infinity;
    for (const corner of corners) {
      const here = this.toLatLon(corner.x, corner.y);
      const t = tileOf(here.lat, here.lon, zoom);
      minTx = Math.min(minTx, Math.floor(t.x));
      maxTx = Math.max(maxTx, Math.floor(t.x));
      minTy = Math.min(minTy, Math.floor(t.y));
      maxTy = Math.max(maxTy, Math.floor(t.y));
    }

    const wide = (maxTx - minTx + 1) * (maxTy - minTy + 1);
    if (wide > TILE_BUDGET) {
      // Too much ground for this zoom. Rather than firing hundreds of requests
      // the layer says nothing, and the next zoom in will be within budget.
      this.clear();
      return;
    }

    const live = new Set<string>();
    for (let tx = minTx; tx <= maxTx; tx++) {
      for (let ty = minTy; ty <= maxTy; ty++) {
        const key = `${zoom}/${tx}/${ty}`;
        live.add(key);
        if (this.failed.has(key)) continue;
        let tile = this.tiles.get(key);
        if (!tile) {
          tile = this.make(zoom, tx, ty, key);
          this.tiles.set(key, tile);
        }
        this.place(tile, viewport);
      }
    }

    for (const [key, tile] of this.tiles) {
      if (live.has(key)) continue;
      this.retire(key, tile);
      this.tiles.delete(key);
    }
  }

  /** One tile as a four-corner quad, so it lands where the roads land. */
  private make(zoom: number, tx: number, ty: number, key: string): Tile {
    const mesh = new MeshSimple({
      texture: Texture.EMPTY,
      vertices: new Float32Array(8),
      uvs: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
      indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });
    this.container.addChild(mesh);

    // Seen before and still held, so nothing is asked for.
    const held = this.cache.get(key);
    if (held) {
      this.cache.delete(key);
      mesh.texture = held;
      return { mesh, z: zoom, x: tx, y: ty };
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.addEventListener('load', () => {
      mesh.texture = Texture.from(image);
    });
    image.addEventListener('error', () => {
      // A tile that will not load stays not-loaded. Retrying on every frame
      // would turn one bad tile into a request storm.
      this.failed.add(key);
    });
    image.src = `${TILE_URL}/${zoom}/${ty}/${tx}`;
    void this.renderer;

    return { mesh, z: zoom, x: tx, y: ty };
  }

  /** Put a tile's four corners where its ground actually is on screen. */
  private place(tile: Tile, viewport: Viewport): void {
    const positions = tile.mesh.vertices as Float32Array;
    const corners: [number, number][] = [
      [tile.x, tile.y],
      [tile.x + 1, tile.y],
      [tile.x + 1, tile.y + 1],
      [tile.x, tile.y + 1],
    ];

    for (let i = 0; i < 4; i++) {
      const corner = corners[i] as [number, number];
      const here = tileCorner(corner[0], corner[1], tile.z);
      const world = this.toWorld(here.lat, here.lon);
      const p = project(this.projection, world.x, world.y);
      positions[i * 2] = viewport.toScreenX(p.sx);
      positions[i * 2 + 1] = viewport.toScreenY(p.sy);
    }
    tile.mesh.vertices = positions;
  }
}
